import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  CategoryKind,
  RecurringDirection,
  RecurringFrequency,
  RecurringSourceKind,
  amountForMonth,
  hasAmountOverride,
  isActiveInMonth,
  isWithinRecurringTerm,
  recurringOccurrenceCount,
  recurringOccurrencesRemaining,
  type AppCurrency,
  type RecurringExpense,
} from '@financial/shared';
import {
  useCreateRecurring,
  useDeleteRecurring,
  useGenerateRecurringOne,
  useRecurring,
  useRunRecurring,
  useSetRecurringMonthAmount,
  useUpcomingRecurringCurrencies,
  useUpdateRecurring,
} from '../features/recurring/useRecurring';
import { useAccounts } from '../features/accounts/useAccounts';
import { useCategories } from '../features/categories/useCategories';
import { useCard } from '../features/creditCard/useCreditCard';
import { useSavings } from '../features/savings/useSavings';
import { useBudget } from '../features/budgets/useBudgets';
import { formatMoney } from '../lib/format';
import { getApiErrorMessage } from '../lib/errors';
import { getPurchasePolicy, safeExtraSavings } from '../lib/purchaseCapacity';
import { recurringFundingIssues, type RecurringFundingIssue } from '../lib/recurringFundingIssues';
import { Field } from '../components/ui/Field';
import { MonthPicker } from '../components/ui/DatePicker';
import { Modal } from '../components/ui/Modal';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { ItemActionsMenu, type ItemAction } from '../components/ui/ItemActionsMenu';
import {
  Button,
  EmptyState,
  FilterChipGroup,
  HelperText,
  SegmentedControl,
  Skeleton,
} from '../components/ui/Primitives';
import { SkeletonState } from '../components/ui/SkeletonState';
import { GuidedPageHeader } from '../components/onboarding/GuidedPageHeader';
import { currentYearMonth } from '../lib/businessDate';
import { Tooltip } from '../components/ui/Tooltip';
import {
  recurringMonthStatus,
  recurringMonthStatusPriority,
  type RecurringMonthLookupState,
  type RecurringMonthStatus,
} from '../lib/recurringMonthStatus';

function formatRecurringMonth(month: string): string {
  const [year, monthNumber] = month.split('-').map(Number);
  if (!year || !monthNumber) return month;
  return new Intl.DateTimeFormat('es-MX', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
    .format(new Date(Date.UTC(year, monthNumber - 1, 1)))
    .replace('.', '');
}

function frequencyLabel(frequency: RecurringFrequency): string {
  return frequency === RecurringFrequency.QUARTERLY ? 'Cada 3 meses' : 'Mensual';
}

type RecurringDirectionTotals = {
  income: number;
  expense: number;
  transfer: number;
};

function formatRecurringTotals(
  byCurrency: Map<AppCurrency, RecurringDirectionTotals>,
  key: keyof RecurringDirectionTotals,
): string {
  const values = [...byCurrency.entries()]
    .map(([currency, totals]) => ({ currency, amount: totals[key] }))
    .filter(({ amount }) => amount !== 0);
  if (values.length === 0) return formatMoney(0);
  if (values.length === 1) return formatMoney(values[0].amount, values[0].currency);
  return values
    .map(({ amount, currency }) => `${formatMoney(amount, currency)} ${currency}`)
    .join(' · ');
}

export function RecurringExpensesPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const businessMonth = currentYearMonth();
  const savingsAutomation = location.state as {
    automateSavings?: boolean;
    suggestedAmount?: number;
    goalId?: string;
  } | null;
  const listQ = useRecurring();
  const items = listQ.data ?? [];
  const recurringCurrencies = useMemo(
    () => [...new Set(items.map((item) => item.currency))].sort(),
    [items],
  );
  const upcomingQueries = useUpcomingRecurringCurrencies(
    businessMonth,
    recurringCurrencies,
    listQ.isSuccess,
  );
  const accountsQ = useAccounts();
  const cardQ = useCard();
  const run = useRunRecurring();
  const [addOpen, setAddOpen] = useState(Boolean(savingsAutomation?.automateSavings));
  const [runConfirmOpen, setRunConfirmOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [status, setStatus] = useState<'all' | 'active' | 'paused' | 'upcoming'>('active');
  const [directionFilter, setDirectionFilter] = useState<'all' | RecurringDirection>('all');
  const requestedRuleId = searchParams.get('editar');
  const requestedAmountId = searchParams.get('ajustar');
  const requestedMonth = /^\d{4}-(0[1-9]|1[0-2])$/.test(searchParams.get('mes') ?? '')
    ? searchParams.get('mes')!
    : businessMonth;
  const clearRequestedEditor = useCallback(() => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('editar');
        next.delete('ajustar');
        next.delete('mes');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);
  const upcomingStateByCurrency = useMemo(
    () => new Map(upcomingQueries.map((query) => [query.currency, query])),
    [upcomingQueries],
  );
  const upcomingItems = useMemo(
    () =>
      upcomingQueries.flatMap((query) =>
        query.data?.month === businessMonth && query.data.currency === query.currency
          ? query.data.items.filter((item) => item.currency === query.currency)
          : [],
      ),
    [businessMonth, upcomingQueries],
  );
  const upcomingCoverageReady = upcomingQueries.every(
    (query) =>
      query.isSuccess &&
      !query.isFetching &&
      query.data?.month === businessMonth &&
      query.data.currency === query.currency,
  );
  const upcomingById = useMemo(
    () => new Map(upcomingItems.map((item) => [item.id, item])),
    [upcomingItems],
  );
  // "No registrado" lo decide el backend por moneda: activo, del mes y aún sin
  // materializar. Mirar sólo el día listaba como pendiente lo ya registrado.
  const pendingIds = useMemo(() => new Set(upcomingItems.map((item) => item.id)), [upcomingItems]);
  const fundingIssues = useMemo(
    () => recurringFundingIssues(upcomingItems, accountsQ.data ?? []),
    [accountsQ.data, upcomingItems],
  );
  const fundingIssuesById = useMemo(
    () => new Map(fundingIssues.map((issue) => [issue.recurringExpenseId, issue])),
    [fundingIssues],
  );
  const monthStatusById = useMemo(() => {
    return new Map(
      items.map((item) => {
        const query = upcomingStateByCurrency.get(item.currency);
        const responseMatches =
          query?.isSuccess &&
          !query.isFetching &&
          query.data?.month === businessMonth &&
          query.data.currency === item.currency;
        const lookupState: RecurringMonthLookupState = responseMatches
          ? 'success'
          : query?.isError || (query?.isSuccess && !query.isFetching)
            ? 'error'
            : 'loading';
        return [
          item.id,
          recurringMonthStatus({
            item,
            month: businessMonth,
            lookupState,
            upcoming: upcomingById.get(item.id),
            hasFundingIssue: fundingIssuesById.has(item.id),
          }),
        ];
      }),
    );
  }, [businessMonth, fundingIssuesById, items, upcomingById, upcomingStateByCurrency]);
  const visibleItems = useMemo(() => {
    // Una regla trimestral sigue activa aunque no le toque cobrar este mes. La
    // dejamos visible para que el usuario pueda revisar o editar su siguiente
    // cargo; `isActiveInMonth` se reserva para las sumas del mes.
    const isCurrent = (item: RecurringExpense) =>
      item.active &&
      (item.startMonth > businessMonth || isWithinRecurringTerm(item, businessMonth));
    const byStatus =
      status === 'active'
        ? items.filter(isCurrent)
        : status === 'paused'
          ? items.filter((item) => !item.active)
          : status === 'upcoming'
            ? items.filter((item) => {
                const monthStatus = monthStatusById.get(item.id);
                return (
                  monthStatus?.kind === 'scheduled' ||
                  monthStatus?.kind === 'due' ||
                  monthStatus?.kind === 'overdue' ||
                  monthStatus?.kind === 'insufficient-funds' ||
                  (!upcomingCoverageReady &&
                    isCurrent(item) &&
                    (monthStatus?.kind === 'checking' || monthStatus?.kind === 'unavailable'))
                );
              })
            : items;
    const byDirection =
      directionFilter === 'all'
        ? byStatus
        : byStatus.filter((item) => item.direction === directionFilter);
    const originalOrder = new Map(items.map((item, index) => [item.id, index]));
    return [...byDirection].sort((left, right) => {
      const leftStatus = monthStatusById.get(left.id);
      const rightStatus = monthStatusById.get(right.id);
      const priorityDelta =
        (leftStatus ? recurringMonthStatusPriority(leftStatus) : 99) -
        (rightStatus ? recurringMonthStatusPriority(rightStatus) : 99);
      if (priorityDelta !== 0) return priorityDelta;
      const leftDue = leftStatus?.upcoming?.dueDate ?? '';
      const rightDue = rightStatus?.upcoming?.dueDate ?? '';
      return (
        leftDue.localeCompare(rightDue) ||
        (originalOrder.get(left.id) ?? 0) - (originalOrder.get(right.id) ?? 0)
      );
    });
  }, [businessMonth, directionFilter, items, monthStatusById, status, upcomingCoverageReady]);
  const recurringSummary = useMemo(() => {
    return visibleItems.reduce(
      (summary, item) => {
        // Una frecuencia trimestral sólo afecta presupuesto y totales el mes
        // que le toca ocurrir; en los meses intermedios sigue visible pero no se
        // suma como gasto o ingreso mensual.
        if (!isActiveInMonth(item, businessMonth)) return summary;
        const amount = amountForMonth(item, businessMonth);
        const totals = summary.byCurrency.get(item.currency) ?? {
          income: 0,
          expense: 0,
          transfer: 0,
        };
        if (item.direction === RecurringDirection.INCOME) totals.income += amount;
        if (item.direction === RecurringDirection.EXPENSE) totals.expense += amount;
        if (item.direction === RecurringDirection.TRANSFER) totals.transfer += amount;
        summary.byCurrency.set(item.currency, totals);
        if (pendingIds.has(item.id)) summary.upcoming += 1;
        return summary;
      },
      {
        byCurrency: new Map<AppCurrency, RecurringDirectionTotals>(),
        upcoming: 0,
      },
    );
  }, [businessMonth, visibleItems, pendingIds]);
  const sourceLabel = useCallback(
    (item: RecurringExpense) => {
      if (item.sourceKind === RecurringSourceKind.CARD) return cardQ.data?.name ?? 'Tarjeta';
      return accountsQ.data?.find((account) => account.id === item.sourceId)?.name ?? 'Cuenta';
    },
    [accountsQ.data, cardQ.data?.name],
  );
  const closeAdd = () => {
    setAddOpen(false);
    if (savingsAutomation?.automateSavings)
      navigate(location.pathname, { replace: true, state: null });
  };

  useEffect(() => {
    if (savingsAutomation?.automateSavings) setAddOpen(true);
  }, [savingsAutomation?.automateSavings]);

  useEffect(() => {
    if (searchParams.get('nuevo') !== '1') return;
    setAddOpen(true);
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('nuevo');
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  // Un enlace desde un movimiento histórico también debe encontrar una
  // programación pausada o cuyo plazo ya terminó.
  useEffect(() => {
    if (!requestedRuleId && !requestedAmountId) return;
    setStatus('all');
    setDirectionFilter('all');
  }, [requestedAmountId, requestedRuleId]);

  return (
    <div className="reference-page w-full">
      <GuidedPageHeader
        section="recurring"
        title="Movimientos fijos"
        description="Programa cargos, ingresos y apartados que se registran automáticamente el día indicado."
        actions={
          <Tooltip
            content="Programa un ingreso, gasto o traspaso para que se registre automáticamente"
            side="bottom"
            align="end"
          >
            <Button onClick={() => setAddOpen(true)}>+ Movimiento</Button>
          </Tooltip>
        }
      />

      <div className="ui-card mb-4">
        <p className="text-sm leading-6 text-slate-600">
          Todos los días a las <strong className="text-slate-800">6:00 a. m.</strong>, hora de
          Ciudad de México, registramos automáticamente los movimientos que correspondan. Si el mes
          no tiene el día elegido, usamos su último día. No necesitas capturarlos manualmente.
        </p>
        <Tooltip content="Registra ahora los movimientos fijos que ya corresponden" align="start">
          <button
            onClick={() => setRunConfirmOpen(true)}
            disabled={run.isPending}
            className="mt-2 text-sm font-medium text-savings disabled:opacity-60"
          >
            {run.isPending ? 'Procesando…' : 'Procesar pendientes de hoy'}
          </button>
        </Tooltip>
        {run.isSuccess && (
          <span role="status" className="ml-2 text-sm text-income">
            {run.data.created === 0
              ? 'Todo está al día'
              : `${run.data.created} ${run.data.created === 1 ? 'movimiento registrado' : 'movimientos registrados'}`}
          </span>
        )}
        {run.isError && (
          <p role="alert" className="mt-2 text-sm text-expense">
            {getApiErrorMessage(run.error)}
          </p>
        )}
      </div>

      {fundingIssues.length > 0 && <RecurringFundingWarning issues={fundingIssues} />}

      <section className="mb-4 space-y-3 lg:flex lg:items-end lg:gap-8 lg:space-y-0">
        <div className="lg:shrink-0">
          <p className="mb-1.5 px-0.5 text-xs font-bold uppercase tracking-[.07em] text-slate-600">
            Estado
          </p>
          <FilterChipGroup
            value={status}
            ariaLabel="Filtrar movimientos fijos por estado"
            options={[
              { value: 'active' as const, label: 'En uso' },
              {
                value: 'upcoming' as const,
                label: 'No registrados',
                count: upcomingCoverageReady ? pendingIds.size : undefined,
              },
              { value: 'paused' as const, label: 'Pausados' },
              { value: 'all' as const, label: 'Todos' },
            ]}
            onChange={setStatus}
          />
        </div>
        <button
          type="button"
          onClick={() => setFiltersOpen((current) => !current)}
          className="button button-secondary w-full justify-between sm:hidden"
          aria-expanded={filtersOpen}
          aria-controls="recurring-advanced-filters"
        >
          <span>Más filtros</span>
          <span className="flex items-center gap-2 text-xs text-slate-600">
            <span
              className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-slate-100 px-1.5 font-bold text-slate-700"
              aria-label={`${directionFilter === 'all' ? 0 : 1} ${directionFilter === 'all' ? 'filtros avanzados activos' : 'filtro avanzado activo'}`}
            >
              {directionFilter === 'all' ? 0 : 1}
            </span>
            <span aria-hidden>{filtersOpen ? '▴' : '▾'}</span>
          </span>
        </button>
        <div
          id="recurring-advanced-filters"
          className={`${filtersOpen ? 'block' : 'hidden'} ui-card p-3 sm:block lg:shrink-0`}
        >
          <p className="mb-1.5 px-0.5 text-xs font-bold uppercase tracking-[.07em] text-slate-600">
            Tipo
          </p>
          <FilterChipGroup
            value={directionFilter}
            ariaLabel="Filtrar movimientos fijos por tipo"
            options={[
              { value: 'all' as const, label: 'Todos' },
              { value: RecurringDirection.EXPENSE, label: 'Cargos' },
              { value: RecurringDirection.INCOME, label: 'Ingresos' },
              { value: RecurringDirection.TRANSFER, label: 'Apartados' },
            ]}
            onChange={setDirectionFilter}
          />
        </div>
      </section>

      {!listQ.isLoading && visibleItems.length > 0 && (
        <section className="mb-4 grid grid-cols-2 overflow-hidden rounded-[18px] border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,.04),0_12px_28px_-18px_rgba(15,23,42,.18)] sm:grid-cols-4">
          <RecurringMetric
            label="Ingresos fijos"
            value={formatRecurringTotals(recurringSummary.byCurrency, 'income')}
            tone="income"
          />
          <RecurringMetric
            label="Cargos fijos"
            value={formatRecurringTotals(recurringSummary.byCurrency, 'expense')}
            tone="expense"
          />
          <RecurringMetric
            label="Apartados"
            value={formatRecurringTotals(recurringSummary.byCurrency, 'transfer')}
            tone="savings"
          />
          <RecurringMetric
            label="Aún no registrados"
            value={upcomingCoverageReady ? `${recurringSummary.upcoming}` : '—'}
            tone="neutral"
          />
        </section>
      )}

      <div className="min-[1400px]:grid min-[1400px]:grid-cols-[minmax(0,1fr)_264px] min-[1400px]:items-start min-[1400px]:gap-4">
        <div className="min-w-0">
          {listQ.isLoading && <RecurringExpensesSkeleton />}
          {items.length === 0 && !listQ.isLoading && (
            <EmptyState
              title="Sin movimientos fijos"
              description="Agrega un cargo, ingreso o apartado automático para planear tu mes."
              action={<Button onClick={() => setAddOpen(true)}>Agregar el primero</Button>}
            />
          )}

          {items.length > 0 && visibleItems.length === 0 && !listQ.isLoading && (
            <p className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
              No hay movimientos fijos con estos filtros.
            </p>
          )}
          {visibleItems.length > 0 && !listQ.isLoading && (
            <div className="hidden xl:grid xl:grid-cols-[minmax(120px,1.2fr)_48px_70px_minmax(82px,.8fr)_78px_132px_160px] xl:items-center xl:gap-3 xl:rounded-t-[16px] xl:border xl:border-b-0 xl:border-slate-200/80 xl:bg-slate-50 xl:px-4 xl:py-3 xl:text-[10px] xl:font-bold xl:uppercase xl:tracking-[.08em] xl:text-slate-500">
              <span>Concepto</span>
              <span>Día</span>
              <span>Plazo</span>
              <span>Se paga de</span>
              <span className="text-right">Monto</span>
              <span>Este mes</span>
              <span className="text-right">Acción</span>
            </div>
          )}
          <ul
            className={`space-y-2 ${visibleItems.length > 0 && !listQ.isLoading ? 'xl:space-y-0 xl:overflow-hidden xl:rounded-[16px] xl:rounded-t-none xl:border xl:border-slate-200/80 xl:bg-white xl:shadow-[0_1px_2px_rgba(15,23,42,.06),0_8px_20px_-12px_rgba(15,23,42,.15)]' : ''}`}
          >
            {visibleItems.map((r) => {
              const autoOpen =
                requestedRuleId === r.id
                  ? 'rule'
                  : requestedAmountId === r.id
                    ? 'amount'
                    : undefined;
              return (
                <RecurringRow
                  key={r.id}
                  item={r}
                  sourceLabel={sourceLabel(r)}
                  monthStatus={
                    monthStatusById.get(r.id) ?? {
                      kind: 'checking',
                    }
                  }
                  fundingIssue={fundingIssuesById.get(r.id)}
                  autoOpen={autoOpen}
                  adjustmentMonth={autoOpen === 'amount' ? requestedMonth : undefined}
                  onAutoOpen={autoOpen ? clearRequestedEditor : undefined}
                />
              );
            })}
          </ul>
        </div>
        {visibleItems.length > 0 && (
          <RecurringDesktopSidebar
            items={visibleItems}
            monthlyTotals={recurringSummary.byCurrency}
            sourceLabel={sourceLabel}
          />
        )}
      </div>

      <Modal
        open={addOpen}
        title={savingsAutomation?.automateSavings ? 'Automatizar ahorro' : 'Nuevo movimiento fijo'}
        onClose={closeAdd}
      >
        <RecurringForm
          onDone={closeAdd}
          savingsPreset={
            savingsAutomation?.automateSavings
              ? { amount: savingsAutomation.suggestedAmount, goalId: savingsAutomation.goalId }
              : undefined
          }
        />
      </Modal>
      <ConfirmDialog
        open={runConfirmOpen}
        title="¿Procesar los pendientes de hoy?"
        description="La automatización ya corre diariamente. Esta acción revisará ahora los movimientos cuya fecha ya llegó y registrará únicamente los que sigan pendientes, sin duplicarlos."
        confirmLabel="Procesar ahora"
        pendingLabel="Procesando…"
        pending={run.isPending}
        tone="neutral"
        footnote={false}
        error={run.isError ? getApiErrorMessage(run.error) : undefined}
        onClose={() => setRunConfirmOpen(false)}
        onConfirm={() => run.mutate(undefined, { onSuccess: () => setRunConfirmOpen(false) })}
      />
    </div>
  );
}

export function RecurringExpensesSkeleton() {
  return (
    <SkeletonState label="Cargando movimientos fijos…" className="space-y-3">
      {Array.from({ length: 4 }, (_, index) => (
        <Skeleton key={index} className="h-24 w-full" />
      ))}
    </SkeletonState>
  );
}

function RecurringFundingWarning({ issues }: { issues: RecurringFundingIssue[] }) {
  return (
    <section
      className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-amber-950 shadow-[0_8px_22px_-18px_rgba(120,53,15,.45)]"
      aria-labelledby="recurring-funding-warning-title"
    >
      <div className="flex items-start gap-3">
        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-sm font-black text-amber-800"
          aria-hidden
        >
          !
        </span>
        <div className="min-w-0 flex-1" role="status" aria-live="polite" aria-atomic="true">
          <h2 id="recurring-funding-warning-title" className="text-sm font-bold">
            {issues.length === 1
              ? 'Un movimiento necesita saldo disponible'
              : `${issues.length} movimientos necesitan saldo disponible`}
          </h2>
          <p className="mt-1 text-xs leading-5 text-amber-900">
            {issues.length === 1
              ? 'Ya llegó su fecha y, con los saldos registrados actualmente, no puede generarse.'
              : 'Sus fechas ya llegaron y, con los saldos registrados actualmente, no pueden generarse.'}{' '}
            El dinero de tus apartados permanece reservado y no se utilizará automáticamente.
          </p>
        </div>
      </div>

      <ul className="mt-3 grid gap-2 lg:grid-cols-2">
        {issues.map((issue) => (
          <li
            key={issue.recurringExpenseId}
            className="rounded-xl border border-amber-200/80 bg-white/75 px-3 py-2.5"
          >
            <p className="text-sm font-semibold text-slate-900">{issue.name}</p>
            <p className="mt-0.5 text-xs leading-5 text-slate-700">
              Necesita {formatMoney(issue.amount, issue.currency)} ·{' '}
              {formatMoney(issue.availableBalance, issue.currency)} disponibles en{' '}
              {issue.accountName} · faltan {formatMoney(issue.shortfall, issue.currency)}.
            </p>
          </li>
        ))}
      </ul>

      <div className="mt-3 flex flex-col gap-2 text-xs sm:flex-row sm:items-center sm:justify-between">
        <p className="leading-5 text-amber-900">
          Revisa el saldo, libera dinero de un apartado, reduce el monto de este mes o cambia la
          cuenta de origen.
        </p>
        <Link
          to="/cuentas"
          className="tap-target inline-flex shrink-0 items-center justify-center rounded-lg border border-amber-300 bg-white px-3 font-semibold text-amber-900 hover:bg-amber-100"
        >
          Revisar cuentas
        </Link>
      </div>
    </section>
  );
}

function recurringDueLabel(dueDate: string): string {
  const calendarDate = /^\d{4}-\d{2}-\d{2}/.exec(dueDate)?.[0];
  if (!calendarDate) return dueDate;
  return new Intl.DateTimeFormat('es-MX', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })
    .format(new Date(`${calendarDate}T12:00:00.000Z`))
    .replace('.', '');
}

function RecurringMonthStatusBadge({
  status,
  item,
}: {
  status: RecurringMonthStatus;
  item: RecurringExpense;
}) {
  const dueLabel = status.upcoming ? recurringDueLabel(status.upcoming.dueDate) : '';
  const presentation: Record<
    RecurringMonthStatus['kind'],
    { label: string; icon?: string; className: string }
  > = {
    paused: {
      label: 'Pausado',
      icon: 'Ⅱ',
      className: 'border-slate-200 bg-slate-100 text-slate-600',
    },
    'not-started': {
      label: `Inicia en ${formatRecurringMonth(item.startMonth)}`,
      className: 'border-slate-200 bg-slate-50 text-slate-600',
    },
    finished: {
      label: 'Finalizado',
      className: 'border-slate-200 bg-slate-50 text-slate-600',
    },
    'not-scheduled': {
      label: 'No corresponde este mes',
      className: 'border-slate-200 bg-slate-50 text-slate-600',
    },
    checking: {
      label: 'Comprobando…',
      className: 'border-slate-200 bg-slate-50 text-slate-600',
    },
    unavailable: {
      label: 'Estado no disponible',
      className: 'border-slate-200 bg-slate-50 text-slate-600',
    },
    registered: {
      label: 'Registrado este mes',
      icon: '✓',
      className: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    },
    scheduled: {
      label: `Programado · ${dueLabel}`,
      icon: '○',
      className: 'border-blue-200 bg-blue-50 text-blue-800',
    },
    due: {
      label: 'Por registrar hoy',
      icon: '!',
      className: 'border-amber-200 bg-amber-50 text-amber-900',
    },
    overdue: {
      label: `Vencido · ${dueLabel}`,
      icon: '!',
      className: 'border-amber-300 bg-amber-50 text-amber-950',
    },
    'insufficient-funds': {
      label: status.upcoming?.daysUntil === 0 ? 'Por registrar hoy' : `Vencido · ${dueLabel}`,
      icon: '!',
      className: 'border-amber-300 bg-amber-50 text-amber-950',
    },
  };
  const current = presentation[status.kind];

  return (
    <span
      data-month-status={status.kind}
      title={current.label}
      className={`inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-bold leading-4 ${current.className}`}
    >
      {current.icon && <span aria-hidden>{current.icon}</span>}
      <span className="truncate">{current.label}</span>
    </span>
  );
}

function RecurringRow({
  item,
  sourceLabel,
  monthStatus,
  fundingIssue,
  autoOpen,
  adjustmentMonth,
  onAutoOpen,
}: {
  item: RecurringExpense;
  sourceLabel: string;
  monthStatus: RecurringMonthStatus;
  fundingIssue?: RecurringFundingIssue;
  /** Acción solicitada desde la bandeja de pendientes. */
  autoOpen?: 'rule' | 'amount';
  adjustmentMonth?: string;
  onAutoOpen?: () => void;
}) {
  const update = useUpdateRecurring();
  const generate = useGenerateRecurringOne();
  const del = useDeleteRecurring();
  const [editOpen, setEditOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [toggleOpen, setToggleOpen] = useState(false);
  const [adjustMonth, setAdjustMonth] = useState(adjustmentMonth ?? currentYearMonth());
  const isIncome = item.direction === RecurringDirection.INCOME;
  const isTransfer = item.direction === RecurringDirection.TRANSFER;
  const frequency = item.frequency ?? RecurringFrequency.MONTHLY;
  const occurrenceCount = recurringOccurrenceCount(item);
  const remaining = recurringOccurrencesRemaining(item);
  const plazo =
    occurrenceCount === null ? 'Sin plazo' : `Cobro ${item.monthsPaid}/${occurrenceCount}`;
  const month = currentYearMonth();
  const occursThisMonth = isActiveInMonth(item, month);
  // Lo que vale ESTE mes: el ajuste manual si existe, si no el monto base.
  const monthAmount = amountForMonth(item, month);
  const adjusted = occursThisMonth && hasAmountOverride(item, month);
  const paused = !item.active;
  // La API expone esta bandera porque `monthsPaid` es un resumen que podría
  // actualizarse después de crear el registro. El fallback conserva la UI
  // segura durante una actualización gradual del backend.
  const hasHistory = item.hasGeneratedHistory ?? item.monthsPaid > 0;
  const isScheduledLater = monthStatus.kind === 'scheduled';
  const canRegister =
    monthStatus.kind === 'scheduled' ||
    monthStatus.kind === 'due' ||
    monthStatus.kind === 'overdue';
  const needsAttention =
    monthStatus.kind === 'due' ||
    monthStatus.kind === 'overdue' ||
    monthStatus.kind === 'insufficient-funds';
  const mobileFundingIssueId = fundingIssue
    ? `recurring-funding-issue-${item.id}-mobile`
    : undefined;
  const desktopFundingIssueId = fundingIssue
    ? `recurring-funding-issue-${item.id}-desktop`
    : undefined;
  const registerTooltip = isScheduledLater
    ? 'Registrar hoy antes de su fecha programada'
    : 'Registrar ahora este movimiento fijo';
  const itemActions: ItemAction[] = [
    {
      label: `Cambiar monto de ${formatRecurringMonth(month)}`,
      description: 'Modifica únicamente este mes, incluso si ya se registró.',
      onSelect: () => {
        setAdjustMonth(month);
        setAdjustOpen(true);
      },
    },
    {
      label: 'Editar próximos movimientos',
      description: 'Cambia el monto habitual, la fecha o la programación futura.',
      onSelect: () => setEditOpen(true),
    },
    {
      label: item.active ? 'Pausar próximos movimientos' : 'Reactivar movimientos',
      description: item.active
        ? 'Conserva el historial y detiene los próximos registros automáticos.'
        : 'Vuelve a registrar automáticamente las próximas fechas.',
      onSelect: () => setToggleOpen(true),
    },
  ];
  if (!hasHistory) {
    itemActions.push({
      label: 'Eliminar movimiento fijo',
      description: 'Disponible porque todavía no tiene movimientos registrados.',
      tone: 'danger',
      onSelect: () => setDeleteOpen(true),
    });
  }

  useEffect(() => {
    if (!autoOpen) return;
    if (autoOpen === 'rule') setEditOpen(true);
    else {
      setAdjustMonth(adjustmentMonth ?? month);
      setAdjustOpen(true);
    }
    onAutoOpen?.();
  }, [adjustmentMonth, autoOpen, month, onAutoOpen]);

  return (
    <li
      className={`relative overflow-hidden px-4 py-3 ui-card xl:grid xl:grid-cols-[minmax(120px,1.2fr)_48px_70px_minmax(82px,.8fr)_78px_132px_160px] xl:items-center xl:gap-3 xl:rounded-none xl:border-x-0 xl:border-t-0 xl:border-b-slate-100 xl:shadow-none xl:last:border-b-0 ${needsAttention ? 'border-l-4 border-l-amber-400 bg-amber-50/[.16]' : monthStatus.kind === 'scheduled' ? 'border-l-4 border-l-blue-300 bg-blue-50/[.12]' : paused ? 'bg-slate-50/70' : ''}`}
    >
      <div className="flex flex-col gap-3 xl:hidden">
        <div className="min-w-0">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <p className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">
              {item.name}
            </p>
            <RecurringMonthStatusBadge status={monthStatus} item={item} />
          </div>
          <p className="mt-1 text-xs text-slate-600">
            Día {item.dayOfMonth} · {frequencyLabel(frequency)} · {plazo}
            {remaining !== null && item.active ? ` · faltan ${remaining}` : ''}
            {' · '}
            {sourceLabel}
          </p>
          {fundingIssue && (
            <p
              id={mobileFundingIssueId}
              className="mt-1 text-xs font-semibold leading-5 text-amber-800"
            >
              Saldo disponible insuficiente ·{' '}
              {formatMoney(fundingIssue.availableBalance, fundingIssue.currency)} en{' '}
              {fundingIssue.accountName} · faltan{' '}
              {formatMoney(fundingIssue.shortfall, fundingIssue.currency)}
            </p>
          )}
        </div>
        <div className="flex w-full items-center gap-2">
          <div className="mr-auto min-w-0 flex-1 text-left">
            <span
              className={`text-sm font-semibold ${
                isTransfer ? 'text-savings' : isIncome ? 'text-income' : 'text-expense'
              }`}
            >
              {isTransfer ? '⇄ ' : isIncome ? '+' : '−'}
              {formatMoney(monthAmount, item.currency)}
            </span>
            {adjusted && (
              <p className="text-[11px] text-savings">
                monto de este mes · habitual {formatMoney(item.amount, item.currency)}
              </p>
            )}
          </div>
          <ItemActionsMenu
            itemLabel={item.name}
            title="Opciones del movimiento"
            actions={itemActions}
          />
        </div>
        {canRegister && (
          <Tooltip content={registerTooltip} align="end">
            <button
              type="button"
              onClick={() => setRegisterOpen(true)}
              disabled={generate.isPending}
              className={`tap-target inline-flex h-11 w-full items-center justify-center rounded-xl border px-4 text-sm font-semibold transition disabled:opacity-50 ${isScheduledLater ? 'border-slate-300 bg-white text-slate-800 hover:bg-slate-50' : 'border-slate-900 bg-slate-900 text-white shadow-sm hover:bg-slate-800'}`}
            >
              {generate.isPending
                ? 'Registrando…'
                : isScheduledLater
                  ? 'Registrar antes'
                  : 'Registrar ahora'}
            </button>
          </Tooltip>
        )}
        {monthStatus.kind === 'insufficient-funds' && (
          <Link
            to="/cuentas"
            aria-describedby={mobileFundingIssueId}
            className="tap-target inline-flex h-11 w-full items-center justify-center rounded-xl border border-amber-300 bg-amber-50 px-4 text-sm font-semibold text-amber-950 hover:bg-amber-100"
          >
            Revisar saldo
          </Link>
        )}
      </div>

      <div className="hidden min-w-0 xl:block">
        <p
          className={`truncate text-sm font-semibold ${paused ? 'text-slate-600' : 'text-slate-800'}`}
        >
          {item.name}
        </p>
        <p className="mt-0.5 truncate text-[11px] text-slate-500">
          {frequencyLabel(frequency)}
          {remaining !== null && item.active ? ` · faltan ${remaining}` : ''}
        </p>
      </div>
      <p className="hidden text-xs text-slate-600 xl:block">Día {item.dayOfMonth}</p>
      <p
        className={`hidden truncate text-xs xl:block ${paused ? 'text-slate-600' : 'text-slate-500'}`}
      >
        {occurrenceCount === null ? 'Sin plazo' : `Mes ${item.monthsPaid} de ${occurrenceCount}`}
      </p>
      <p className="hidden truncate text-xs text-slate-600 xl:block">{sourceLabel}</p>
      <p
        className={`hidden text-right text-sm font-bold tabular-nums xl:block ${
          paused
            ? 'text-slate-600'
            : isTransfer
              ? 'text-savings'
              : isIncome
                ? 'text-income'
                : 'text-slate-900'
        }`}
      >
        {formatMoney(monthAmount, item.currency)}
      </p>
      <div className="hidden min-w-0 xl:block">
        <RecurringMonthStatusBadge status={monthStatus} item={item} />
        {fundingIssue && (
          <p
            id={desktopFundingIssueId}
            className="mt-1 truncate text-[10px] font-semibold text-amber-800"
            title={`Saldo insuficiente; faltan ${formatMoney(fundingIssue.shortfall, fundingIssue.currency)}`}
          >
            Sin saldo · faltan {formatMoney(fundingIssue.shortfall, fundingIssue.currency)}
          </p>
        )}
      </div>
      <div className="hidden items-center justify-end gap-1 xl:flex">
        {canRegister && (
          <Tooltip content={registerTooltip} align="end">
            <button
              type="button"
              onClick={() => setRegisterOpen(true)}
              disabled={generate.isPending}
              className={`tap-target inline-flex h-9 min-w-[112px] items-center justify-center rounded-lg border px-3 text-xs font-semibold transition disabled:opacity-50 ${isScheduledLater ? 'border-slate-300 bg-white text-slate-800 hover:bg-slate-50' : 'border-slate-900 bg-slate-900 text-white shadow-sm hover:bg-slate-800'}`}
            >
              {generate.isPending
                ? 'Registrando…'
                : isScheduledLater
                  ? 'Registrar antes'
                  : 'Registrar ahora'}
            </button>
          </Tooltip>
        )}
        {monthStatus.kind === 'insufficient-funds' && (
          <Link
            to="/cuentas"
            aria-describedby={desktopFundingIssueId}
            className="tap-target inline-flex h-9 min-w-[112px] items-center justify-center rounded-lg border border-amber-300 bg-amber-50 px-3 text-xs font-semibold text-amber-950 hover:bg-amber-100"
          >
            Revisar saldo
          </Link>
        )}
        <ItemActionsMenu
          itemLabel={item.name}
          title="Opciones del movimiento"
          actions={itemActions}
        />
      </div>

      <Modal
        open={editOpen}
        title="Editar próximos movimientos"
        eyebrow={item.name}
        onClose={() => setEditOpen(false)}
      >
        <RecurringForm item={item} onDone={() => setEditOpen(false)} />
      </Modal>
      <Modal
        open={adjustOpen}
        title={`Cambiar monto de ${formatRecurringMonth(adjustMonth)}`}
        eyebrow={item.name}
        onClose={() => setAdjustOpen(false)}
      >
        <MonthAmountForm
          item={item}
          initialMonth={adjustMonth}
          onMonthChange={setAdjustMonth}
          onDone={() => setAdjustOpen(false)}
        />
      </Modal>
      {!hasHistory && (
        <ConfirmDialog
          open={deleteOpen}
          title="¿Eliminar movimiento fijo?"
          description="Se eliminará su programación. Esta opción sólo está disponible porque todavía no ha generado ningún movimiento."
          itemLabel={item.name}
          pending={del.isPending}
          error={del.isError ? getApiErrorMessage(del.error) : undefined}
          onClose={() => setDeleteOpen(false)}
          onConfirm={() => del.mutate(item.id, { onSuccess: () => setDeleteOpen(false) })}
        />
      )}
      <ConfirmDialog
        open={registerOpen}
        title={isScheduledLater ? '¿Registrar antes de la fecha?' : '¿Registrar este movimiento?'}
        description={
          isScheduledLater
            ? 'Creará el movimiento real con la fecha de hoy y la automatización no lo repetirá el día programado. Hazlo sólo si ya ocurrió o quieres adelantarlo.'
            : 'Creará ahora el movimiento real y actualizará los saldos correspondientes. La automatización no lo duplicará.'
        }
        itemLabel={`${item.name} · ${formatMoney(monthAmount, item.currency)} · ${frequencyLabel(frequency)} · día ${item.dayOfMonth}`}
        confirmLabel="Registrar ahora"
        pendingLabel="Registrando…"
        pending={generate.isPending}
        tone="warning"
        footnote="Al terminar podrás usar Deshacer desde la notificación."
        error={generate.isError ? getApiErrorMessage(generate.error) : undefined}
        onClose={() => setRegisterOpen(false)}
        onConfirm={() =>
          generate.mutate({ id: item.id, month }, { onSuccess: () => setRegisterOpen(false) })
        }
      />
      <ConfirmDialog
        open={toggleOpen}
        title={item.active ? '¿Pausar los próximos movimientos?' : '¿Reactivar este movimiento?'}
        description={
          item.active
            ? 'Dejará de registrarse automáticamente desde su próxima fecha. Todo el historial anterior se conserva.'
            : 'Volverá a registrarse automáticamente en las próximas fechas de su programación.'
        }
        itemLabel={item.name}
        confirmLabel={item.active ? 'Pausar próximos' : 'Reactivar'}
        pendingLabel={item.active ? 'Pausando…' : 'Reactivando…'}
        pending={update.isPending}
        tone="neutral"
        footnote={false}
        error={update.isError ? getApiErrorMessage(update.error) : undefined}
        onClose={() => setToggleOpen(false)}
        onConfirm={() =>
          update.mutate(
            { id: item.id, body: { active: !item.active } },
            { onSuccess: () => setToggleOpen(false) },
          )
        }
      />
    </li>
  );
}

function RecurringMetric({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'income' | 'expense' | 'savings' | 'neutral';
}) {
  const color =
    tone === 'income'
      ? 'text-income'
      : tone === 'expense'
        ? 'text-expense'
        : tone === 'savings'
          ? 'text-savings'
          : 'text-slate-900';
  return (
    <div className="min-w-0 border-b border-slate-100 px-4 py-3 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0">
      <p className="text-xs font-bold uppercase tracking-[.07em] text-slate-600">{label}</p>
      <p
        title={value}
        className={`mt-1 truncate text-lg font-bold tracking-tight tabular-nums ${color}`}
      >
        {value}
      </p>
    </div>
  );
}

function RecurringDesktopSidebar({
  items,
  monthlyTotals,
  sourceLabel,
}: {
  items: RecurringExpense[];
  monthlyTotals: Map<AppCurrency, RecurringDirectionTotals>;
  sourceLabel: (item: RecurringExpense) => string;
}) {
  const month = currentYearMonth();
  const calendarItems = items
    .filter(
      (item) =>
        item.active &&
        item.direction === RecurringDirection.EXPENSE &&
        isActiveInMonth(item, month),
    )
    .sort((a, b) => a.dayOfMonth - b.dayOfMonth)
    .slice(0, 6);
  const activeCount = items.filter((item) => item.active).length;
  const pausedCount = items.length - activeCount;
  const expenseTotals = [...monthlyTotals.entries()]
    .map(([currency, totals]) => ({ currency, amount: totals.expense }))
    .filter(({ amount }) => amount !== 0);
  const multipleCurrencies = expenseTotals.length > 1;
  const singleExpense = expenseTotals[0];
  const largestComparableCharge = !multipleCurrencies
    ? calendarItems
        .filter((item) => !singleExpense || item.currency === singleExpense.currency)
        .reduce<RecurringExpense | undefined>((largest, item) => {
          if (!largest) return item;
          return amountForMonth(item, month) > amountForMonth(largest, month) ? item : largest;
        }, undefined)
    : undefined;

  return (
    <aside className="hidden space-y-4 min-[1400px]:block">
      <section className="rounded-[16px] border border-slate-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,.06),0_8px_20px_-12px_rgba(15,23,42,.15)]">
        <p className="text-xs text-slate-500">Compromiso mensual activo</p>
        {multipleCurrencies ? (
          <dl className="mt-3 space-y-1.5">
            {expenseTotals.map(({ amount, currency }) => (
              <div key={currency} className="flex items-baseline justify-between gap-2">
                <dt className="text-[10px] font-bold text-slate-500">{currency}</dt>
                <dd className="text-sm font-bold tabular-nums text-slate-900">
                  {formatMoney(amount, currency)}
                </dd>
              </div>
            ))}
          </dl>
        ) : (
          <>
            <p className="mt-1 text-[28px] font-bold leading-none tracking-tight tabular-nums text-slate-900">
              {singleExpense
                ? formatMoney(singleExpense.amount, singleExpense.currency)
                : formatMoney(0)}
            </p>
            <div className="mt-4 flex h-2 overflow-hidden rounded-full bg-slate-100">
              {calendarItems.length > 0 && singleExpense ? (
                calendarItems
                  .filter((item) => item.currency === singleExpense.currency)
                  .map((item) => (
                    <span
                      key={item.id}
                      className="h-full border-r-2 border-white last:border-r-0"
                      style={{
                        width: `${Math.max(7, (amountForMonth(item, month) / Math.max(1, singleExpense.amount)) * 100)}%`,
                        backgroundColor:
                          item.sourceKind === RecurringSourceKind.CARD ? '#2563eb' : '#0f172a',
                      }}
                    />
                  ))
              ) : (
                <span className="h-full w-full bg-slate-200" />
              )}
            </div>
          </>
        )}
        {multipleCurrencies && (
          <p className="mt-2 text-[10px] leading-4 text-slate-500">Sin convertir monedas.</p>
        )}
        <p className="mt-2 text-xs text-slate-600">
          {activeCount} en uso
          {pausedCount > 0 ? ` · ${pausedCount} pausado${pausedCount === 1 ? '' : 's'}` : ''}
        </p>
      </section>

      <section className="rounded-[16px] border border-slate-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,.06),0_8px_20px_-12px_rgba(15,23,42,.15)]">
        <h2 className="text-[15px] font-semibold text-slate-900">Calendario del mes</h2>
        {calendarItems.length > 0 ? (
          <ul className="mt-3 space-y-3">
            {calendarItems.map((item) => {
              const amount = amountForMonth(item, month);
              return (
                <li key={item.id} className="flex items-center gap-2.5">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-xs font-bold text-slate-600">
                    {item.dayOfMonth}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-medium text-slate-700">
                      {item.name}
                    </span>
                    <span className="block truncate text-xs text-slate-600">
                      {sourceLabel(item)}
                    </span>
                  </span>
                  <span className="shrink-0 text-xs font-bold tabular-nums text-slate-900">
                    {formatMoney(amount, item.currency)}
                    {multipleCurrencies ? ` ${item.currency}` : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="mt-3 text-xs leading-5 text-slate-600">
            No hay cargos fijos programados este mes.
          </p>
        )}
        {calendarItems.length > 0 && (
          <div className="mt-4 border-t border-slate-100 pt-3 text-xs text-slate-600">
            {multipleCurrencies || !largestComparableCharge
              ? 'Importes mostrados en la moneda de cada cuenta.'
              : `Mayor cargo: ${formatMoney(
                  amountForMonth(largestComparableCharge, month),
                  largestComparableCharge.currency,
                )}`}
          </div>
        )}
      </section>
    </aside>
  );
}

/**
 * Ajusta el monto real de un mes concreto, para los recurrentes que no son fijos
 * (una nómina con bonos, por ejemplo). Sirve tanto para corregir un mes ya
 * pasado como para dejar apuntado por adelantado un mes que sabes que vendrá
 * distinto.
 */
function MonthAmountForm({
  item,
  initialMonth = currentYearMonth(),
  onMonthChange,
  onDone,
}: {
  item: RecurringExpense;
  /** La bandeja abre directamente el mes que el usuario estaba consultando. */
  initialMonth?: string;
  onMonthChange?: (month: string) => void;
  onDone: () => void;
}) {
  const setAmount = useSetRecurringMonthAmount();
  const [month, setMonth] = useState(initialMonth);
  const [value, setValue] = useState(String(amountForMonth(item, initialMonth)));
  const overrides = Object.entries(item.amountByMonth ?? {}).sort(([a], [b]) => a.localeCompare(b));
  const isAdjusted = hasAmountOverride(item, month);
  const currentAmount = amountForMonth(item, month);

  const pickMonth = (next: string) => {
    setMonth(next);
    setValue(String(amountForMonth(item, next)));
    onMonthChange?.(next);
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const amount = Number(value);
    if (!Number.isFinite(amount) || amount < 0) return;
    setAmount.mutate({ id: item.id, month, amount }, { onSuccess: onDone });
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <HelperText tone="info">
        Monto habitual: <strong>{formatMoney(item.amount)}</strong>. Este cambio sólo aplica a{' '}
        <strong>{formatRecurringMonth(month)}</strong>; si ya se registró, también actualiza el
        movimiento y el saldo correspondiente.
      </HelperText>

      <MonthPicker
        id={`m-${item.id}`}
        label="Mes"
        required
        value={month}
        onChange={pickMonth}
        className="w-full"
      />
      <Field
        id={`a-${item.id}`}
        label={`Monto de ${formatRecurringMonth(month)}`}
        type="number"
        inputMode="decimal"
        step="0.01"
        min="0"
        required
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      <FixedImpactPreview
        direction={item.direction}
        currentAmount={currentAmount}
        nextAmount={Number(value) || 0}
        month={month}
      />

      {overrides.length > 0 && (
        <div className="rounded-lg bg-slate-50 p-3">
          <p className="mb-1 text-xs font-semibold text-slate-500">Montos específicos por mes</p>
          <ul className="space-y-1">
            {overrides.map(([m, amount]) => (
              <li key={m} className="flex items-center justify-between text-sm text-slate-700">
                <button
                  type="button"
                  data-form-value-change={m === month ? undefined : 'true'}
                  onClick={() => pickMonth(m)}
                  className="font-medium text-savings"
                >
                  {m}
                </button>
                <span>{formatMoney(amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {setAmount.isError && (
        <p role="alert" className="text-sm text-expense">
          {getApiErrorMessage(setAmount.error)}
        </p>
      )}

      <div className="sticky bottom-0 z-10 -mx-1 flex gap-2 border-t border-slate-100 bg-white/95 px-1 pb-1 pt-3 backdrop-blur">
        <button
          type="submit"
          disabled={setAmount.isPending}
          className="tap-target flex-1 rounded-lg bg-slate-900 px-4 py-2.5 text-base font-semibold text-white disabled:opacity-60"
        >
          {setAmount.isPending ? 'Guardando…' : `Guardar monto de ${formatRecurringMonth(month)}`}
        </button>
        {isAdjusted && (
          <button
            type="button"
            disabled={setAmount.isPending}
            onClick={() =>
              setAmount.mutate({ id: item.id, month, amount: null }, { onSuccess: onDone })
            }
            className="tap-target rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-600 disabled:opacity-60"
          >
            Usar monto habitual
          </button>
        )}
      </div>
    </form>
  );
}

function RecurringForm({
  onDone,
  savingsPreset,
  item,
}: {
  onDone: () => void;
  savingsPreset?: { amount?: number; goalId?: string };
  /** Si existe, edita una regla sin tocar las ocurrencias ya materializadas. */
  item?: RecurringExpense;
}) {
  const create = useCreateRecurring();
  const update = useUpdateRecurring();
  const accountsQ = useAccounts();
  const categoriesQ = useCategories();
  const cardQ = useCard();
  const savingsQ = useSavings();
  const editing = Boolean(item);

  const [name, setName] = useState(item?.name ?? (savingsPreset ? 'Ahorro automático' : ''));
  const [amount, setAmount] = useState(
    item
      ? String(item.amount)
      : savingsPreset?.amount
        ? String(Math.round(savingsPreset.amount * 100) / 100)
        : '',
  );
  const [direction, setDirection] = useState<RecurringDirection>(
    item?.direction ?? (savingsPreset ? RecurringDirection.TRANSFER : RecurringDirection.EXPENSE),
  );
  const [categoryId, setCategoryId] = useState(item?.categoryId ?? '');
  const [sourceKind, setSourceKind] = useState<RecurringSourceKind>(
    item?.sourceKind ?? RecurringSourceKind.ACCOUNT,
  );
  const [sourceId, setSourceId] = useState(item?.sourceId ?? '');
  const [destinationId, setDestinationId] = useState(item?.destinationId ?? '');
  const [destinationBucketId, setDestinationBucketId] = useState(item?.destinationBucketId ?? '');
  const [dayOfMonth, setDayOfMonth] = useState(String(item?.dayOfMonth ?? 1));
  const [frequency, setFrequency] = useState<RecurringFrequency>(
    item?.frequency ?? RecurringFrequency.MONTHLY,
  );
  const [startMonth, setStartMonth] = useState(item?.startMonth ?? currentYearMonth());
  const [indefinite, setIndefinite] = useState(item ? item.termMonths === null : true);
  const [termMonths, setTermMonths] = useState(
    item?.termMonths !== null && item?.termMonths !== undefined ? String(item.termMonths) : '12',
  );

  const accounts = accountsQ.data ?? [];
  const card = cardQ.data;
  const savingsGoal = (savingsQ.data ?? []).find((goal) => goal.id === savingsPreset?.goalId);
  const goalDestination = useMemo(() => {
    if (!savingsPreset?.goalId) return undefined;
    for (const account of accounts) {
      const bucket = account.buckets.find((item) => item.goalId === savingsPreset.goalId);
      if (bucket) return { account, bucket };
    }
    return undefined;
  }, [accounts, savingsPreset?.goalId]);
  const destinationBuckets = useMemo(
    () => accounts.find((account) => account.id === destinationId)?.buckets ?? [],
    [accounts, destinationId],
  );
  const categories = useMemo(() => {
    if (direction === RecurringDirection.TRANSFER) return [];
    const kind =
      direction === RecurringDirection.INCOME ? CategoryKind.INCOME : CategoryKind.EXPENSE;
    return (categoriesQ.data ?? []).filter((category) => category.kind === kind);
  }, [categoriesQ.data, direction]);
  const hasGeneratedHistory = item?.hasGeneratedHistory ?? Boolean(item && item.monthsPaid > 0);
  const canChangeFrequency = !editing || !hasGeneratedHistory;
  const finiteTermMonths = Number(termMonths);
  const quarterlyTermIsValid =
    indefinite ||
    frequency !== RecurringFrequency.QUARTERLY ||
    (Number.isInteger(finiteTermMonths) && finiteTermMonths >= 3 && finiteTermMonths % 3 === 0);
  const numericAmount = Number(amount);
  const amountIsValid = amount.trim() !== '' && Number.isFinite(numericAmount) && numericAmount > 0;
  const schedulePreview = {
    amount: amountIsValid ? numericAmount : 0,
    startMonth,
    frequency,
    termMonths: indefinite ? null : Number(termMonths) || 1,
    monthsPaid: item?.monthsPaid ?? 0,
    active: true,
  };
  const occursThisMonthAfterSave = isActiveInMonth(schedulePreview, currentYearMonth());

  useEffect(() => {
    if (savingsPreset?.goalId && goalDestination) {
      setDestinationId(goalDestination.account.id);
      setDestinationBucketId(goalDestination.bucket.id);
    }
  }, [goalDestination, savingsPreset?.goalId]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !amountIsValid) return;
    const resolvedSource =
      sourceKind === RecurringSourceKind.CARD ? card?.id : sourceId || accounts[0]?.id;
    if (!resolvedSource) return;
    if (direction === RecurringDirection.TRANSFER && !destinationId) return;
    if (savingsPreset?.goalId && !destinationBucketId) return;
    if (!quarterlyTermIsValid) return;
    const shared = {
      name: name.trim(),
      amount: numericAmount,
      categoryId: direction === RecurringDirection.TRANSFER ? null : categoryId || null,
      sourceKind,
      sourceId: resolvedSource,
      destinationId: direction === RecurringDirection.TRANSFER ? destinationId : null,
      destinationBucketId:
        direction === RecurringDirection.TRANSFER ? destinationBucketId || null : null,
      dayOfMonth: Number(dayOfMonth) || 1,
      ...(canChangeFrequency ? { frequency } : {}),
      termMonths: indefinite ? null : Number(termMonths) || 1,
    };
    if (item) {
      update.mutate({ id: item.id, body: shared }, { onSuccess: onDone });
      return;
    }
    create.mutate(
      {
        ...shared,
        categoryId: shared.categoryId ?? undefined,
        direction,
        startMonth,
      },
      { onSuccess: onDone },
    );
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {savingsPreset && (
        <HelperText tone="success">
          {goalDestination && savingsGoal
            ? `Cada ejecución llegará al apartado “${goalDestination.bucket.name}” y avanzará la meta “${savingsGoal.name}” con dinero real.`
            : 'Para automatizar una meta necesitas primero un apartado vinculado a ella. Así el dinero queda reservado y el avance no es sólo una cifra.'}
        </HelperText>
      )}
      {editing ? (
        <HelperText tone="info">
          Estos cambios se aplican a los próximos movimientos. No modifican meses ya registrados. Si
          sólo cambiará {formatRecurringMonth(currentYearMonth())}, usa{' '}
          <strong>Cambiar monto de este mes</strong> desde el menú del movimiento.
        </HelperText>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            data-form-value-change={direction === RecurringDirection.EXPENSE ? undefined : 'true'}
            onClick={() => {
              setDirection(RecurringDirection.EXPENSE);
              setCategoryId('');
            }}
            className={`tap-target rounded-lg py-2 text-sm font-semibold ${
              direction === RecurringDirection.EXPENSE
                ? 'bg-expense text-white'
                : 'bg-slate-100 text-slate-600'
            }`}
          >
            Cargo fijo
          </button>
          <button
            type="button"
            data-form-value-change={direction === RecurringDirection.INCOME ? undefined : 'true'}
            onClick={() => {
              setDirection(RecurringDirection.INCOME);
              setCategoryId('');
              if (sourceKind === RecurringSourceKind.CARD) {
                setSourceKind(RecurringSourceKind.ACCOUNT);
              }
            }}
            className={`tap-target rounded-lg py-2 text-sm font-semibold ${
              direction === RecurringDirection.INCOME
                ? 'bg-income text-white'
                : 'bg-slate-100 text-slate-600'
            }`}
          >
            Ingreso fijo
          </button>
          <button
            type="button"
            data-form-value-change={direction === RecurringDirection.TRANSFER ? undefined : 'true'}
            onClick={() => {
              setDirection(RecurringDirection.TRANSFER);
              setSourceKind(RecurringSourceKind.ACCOUNT);
              setCategoryId('');
            }}
            className={`tap-target rounded-lg py-2 text-sm font-semibold ${
              direction === RecurringDirection.TRANSFER
                ? 'bg-savings text-white'
                : 'bg-slate-100 text-slate-600'
            }`}
          >
            Apartado
          </button>
        </div>
      )}
      {direction === RecurringDirection.TRANSFER && (
        <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
          Un apartado recurrente mueve dinero a una cuenta o apartado tuyo según la frecuencia que
          elijas. No cuenta como gasto: se reserva como ahorro, no se suma dos veces.
        </p>
      )}

      <Field
        id="r-name"
        label="Nombre"
        required
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <Field
        id="r-amount"
        label="Monto"
        type="number"
        inputMode="decimal"
        step="0.01"
        min="0.01"
        required
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
      />
      {direction !== RecurringDirection.TRANSFER && (
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Categoría</span>
          <select
            className="tap-target w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-base"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">Sin categoría</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.icon ? `${category.icon} ` : ''}
                {category.name}
              </option>
            ))}
          </select>
          <span className="mt-1 block text-xs leading-5 text-slate-500">
            Ayuda a que el presupuesto y las alertas clasifiquen este compromiso.
          </span>
        </label>
      )}
      {!editing && (
        <FixedImpactPreview
          direction={direction}
          currentAmount={0}
          nextAmount={occursThisMonthAfterSave ? Number(amount) || 0 : 0}
        />
      )}

      <label className="block">
        <span className="mb-1 block text-sm font-medium text-slate-700">
          {direction === RecurringDirection.INCOME
            ? 'Se recibe en'
            : direction === RecurringDirection.TRANSFER
              ? 'Sale de'
              : 'Se paga desde'}
        </span>
        <select
          className="tap-target w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-base"
          value={sourceKind === RecurringSourceKind.CARD ? 'card' : sourceId}
          onChange={(e) => {
            if (e.target.value === 'card') {
              setSourceKind(RecurringSourceKind.CARD);
            } else {
              setSourceKind(RecurringSourceKind.ACCOUNT);
              setSourceId(e.target.value);
            }
          }}
          required
        >
          <option value="" disabled>
            Elige origen
          </option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
          {card && direction === RecurringDirection.EXPENSE && (
            <option value="card">💳 {card.name}</option>
          )}
        </select>
      </label>

      {direction === RecurringDirection.TRANSFER &&
        (savingsPreset?.goalId ? (
          goalDestination ? (
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-3 text-sm text-slate-700">
              <p className="font-semibold text-slate-800">Destino de la meta</p>
              <p className="mt-1">
                {goalDestination.account.name} · apartado {goalDestination.bucket.name}
              </p>
              <p className="mt-1 text-xs leading-5 text-slate-500">
                No se puede cambiar aquí: esta automatización debe entrar al apartado que respalda
                la meta.
              </p>
            </div>
          ) : (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <p className="font-semibold">Falta un apartado vinculado a esta meta.</p>
              <p className="mt-1 text-xs leading-5">
                Créalo desde Cuentas y vincúlalo a “{savingsGoal?.name ?? 'esta meta'}”; después
                vuelve a automatizar el ahorro.
              </p>
              <Link
                to="/cuentas"
                className="tap-target mt-2 inline-flex items-center text-sm font-semibold text-savings"
              >
                Ir a Cuentas →
              </Link>
            </div>
          )
        ) : (
          <>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">Se ahorra en</span>
              <select
                className="tap-target w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-base"
                value={destinationId}
                onChange={(e) => {
                  setDestinationId(e.target.value);
                  setDestinationBucketId('');
                }}
                required
              >
                <option value="" disabled>
                  Elige cuenta destino
                </option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">
                Apartado destino (opcional)
              </span>
              <select
                className="tap-target w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-base"
                value={destinationBucketId}
                onChange={(e) => setDestinationBucketId(e.target.value)}
                disabled={!destinationId}
              >
                <option value="">Sin apartado específico</option>
                {destinationBuckets.map((bucket) => (
                  <option key={bucket.id} value={bucket.id}>
                    {bucket.name}
                    {bucket.goalId ? ' · meta vinculada' : ''}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs leading-5 text-slate-500">
                Elige uno para dejar el dinero reservado. Si está ligado a una meta, su avance se
                actualizará solo.
              </span>
            </label>
          </>
        ))}

      <section
        className="rounded-xl border border-slate-200 bg-slate-50/60 p-3"
        aria-labelledby="r-frequency-label"
      >
        <p id="r-frequency-label" className="text-sm font-semibold text-slate-800">
          Frecuencia
        </p>
        {canChangeFrequency ? (
          <SegmentedControl
            className="mt-2"
            value={frequency}
            ariaLabel="Elegir frecuencia del movimiento fijo"
            options={[
              { value: RecurringFrequency.MONTHLY, label: 'Mensual' },
              { value: RecurringFrequency.QUARTERLY, label: 'Cada 3 meses' },
            ]}
            onChange={(nextFrequency) => {
              setFrequency(nextFrequency);
              if (
                nextFrequency === RecurringFrequency.QUARTERLY &&
                !indefinite &&
                Number(termMonths) % 3 !== 0
              ) {
                setTermMonths('12');
              }
            }}
          />
        ) : (
          <p className="mt-1 text-sm font-medium text-slate-700">{frequencyLabel(frequency)}</p>
        )}
        <p className="mt-2 text-xs leading-5 text-slate-500">
          {frequency === RecurringFrequency.QUARTERLY
            ? `Se registrará cada tres meses, el día ${dayOfMonth || '1'}, empezando en ${formatRecurringMonth(startMonth)}.`
            : `Se registrará el día ${dayOfMonth || '1'} de cada mes${editing ? '' : `, empezando en ${formatRecurringMonth(startMonth)}`}.`}
        </p>
        {!canChangeFrequency && (
          <p className="mt-1 text-xs leading-5 text-slate-500">
            La frecuencia no se cambia porque ya existen movimientos registrados. Crea otro
            movimiento fijo si necesitas una frecuencia distinta.
          </p>
        )}
      </section>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          id="r-day"
          label="Día del cobro"
          type="number"
          min="1"
          max="31"
          value={dayOfMonth}
          onChange={(e) => setDayOfMonth(e.target.value)}
          helperText="Si el mes no tiene ese día, se registra el último día disponible."
        />
        {!editing && (
          <MonthPicker
            id="r-start-month"
            label="Primer mes"
            required
            value={startMonth}
            onChange={setStartMonth}
            helperText="Marca el primer cobro; en trimestrales define octubre, enero, abril…"
            className="w-full"
          />
        )}
        {!indefinite && (
          <Field
            id="r-term"
            label={
              frequency === RecurringFrequency.QUARTERLY ? 'Duración (meses)' : 'Plazo (meses)'
            }
            type="number"
            min={frequency === RecurringFrequency.QUARTERLY ? '3' : '1'}
            step={frequency === RecurringFrequency.QUARTERLY ? '3' : '1'}
            required
            value={termMonths}
            onChange={(e) => setTermMonths(e.target.value)}
            helperText={
              frequency === RecurringFrequency.QUARTERLY
                ? 'Usa múltiplos de 3: 3, 6, 9, 12…'
                : undefined
            }
          />
        )}
      </div>

      {!quarterlyTermIsValid && (
        <p role="alert" className="text-sm text-expense">
          Para un movimiento trimestral, la duración debe ser múltiplo de 3 meses.
        </p>
      )}

      <label className="flex min-h-11 cursor-pointer items-center gap-3 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={indefinite}
          onChange={(e) => setIndefinite(e.target.checked)}
          className="h-5 w-5 shrink-0"
        />
        Sin plazo (indefinido, p. ej. nómina)
      </label>

      {(editing ? update.isError : create.isError) && (
        <p role="alert" className="text-sm text-expense">
          {getApiErrorMessage(editing ? update.error : create.error)}
        </p>
      )}
      <div className="sticky bottom-0 z-10 -mx-1 border-t border-slate-100 bg-white/95 px-1 pb-1 pt-3 backdrop-blur">
        <button
          type="submit"
          disabled={
            (editing ? update.isPending : create.isPending) ||
            !name.trim() ||
            !amountIsValid ||
            Boolean(savingsPreset?.goalId && !goalDestination) ||
            !quarterlyTermIsValid
          }
          className="tap-target w-full rounded-lg bg-slate-900 px-4 py-2.5 text-base font-semibold text-white disabled:opacity-60"
        >
          {editing
            ? update.isPending
              ? 'Guardando…'
              : 'Guardar cambios'
            : create.isPending
              ? 'Guardando…'
              : savingsPreset
                ? 'Crear ahorro automático'
                : 'Guardar'}
        </button>
      </div>
    </form>
  );
}

/** Vista previa antes de crear o ajustar un compromiso que altera el flujo. */
function FixedImpactPreview({
  direction,
  currentAmount,
  nextAmount,
  month = currentYearMonth(),
}: {
  direction: RecurringDirection;
  currentAmount: number;
  nextAmount: number;
  month?: string;
}) {
  const budgetQ = useBudget(month);
  const cardQ = useCard();
  const budget = budgetQ.data;
  const change = nextAmount - currentAmount;

  if (!budget || Math.abs(change) < 0.005) return null;

  // Un apartado sólo quita margen adicional cuando rebasa el ahorro que ya
  // protegía el presupuesto. Así no se descuenta dos veces.
  const savingsTargetAfter =
    direction === RecurringDirection.TRANSFER
      ? Math.max(budget.breakdown.savingsTarget, budget.breakdown.committedSavings + change)
      : budget.breakdown.savingsTarget;
  const extraSavingsImpact = savingsTargetAfter - budget.breakdown.savingsTarget;
  const projectedMargin =
    budget.availableFree +
    (direction === RecurringDirection.INCOME ? change : 0) -
    (direction === RecurringDirection.EXPENSE ? change : 0) -
    extraSavingsImpact;
  const currentCapacity = cardQ.data
    ? getPurchasePolicy(cardQ.data, budget).cashCap
    : safeExtraSavings(budget.availableFree);
  const nextCapacity = cardQ.data
    ? getPurchasePolicy(cardQ.data, budget, { monthlyMargin: projectedMargin }).cashCap
    : safeExtraSavings(projectedMargin);
  const capacityChange = nextCapacity - currentCapacity;
  const currentLimitResult =
    budget.availableFree < 0
      ? `Exceso: ${formatMoney(Math.abs(budget.availableFree))}`
      : `Restante: ${formatMoney(budget.availableFree)}`;
  const projectedLimitResult =
    projectedMargin < 0
      ? `Exceso: ${formatMoney(Math.abs(projectedMargin))}`
      : `Restante: ${formatMoney(projectedMargin)}`;

  return (
    <aside className="rounded-xl border border-blue-100 bg-blue-50/70 p-3 text-sm text-slate-700">
      <p className="font-semibold text-slate-800">Impacto estimado del período</p>
      <p className="mt-1 leading-5">
        El resultado frente a tu límite mensual de gasto cambia de {currentLimitResult} a{' '}
        <span
          className={
            projectedMargin < 0 ? 'font-semibold text-expense' : 'font-semibold text-income'
          }
        >
          {projectedLimitResult}
        </span>
        .
      </p>
      <p className="mt-1 text-xs leading-5 text-slate-500">
        {capacityChange === 0
          ? 'Tu ahorro objetivo actual ya absorbe este apartado; no cambia el tope prudente de compra.'
          : `El espacio prudente para una compra nueva ${capacityChange > 0 ? 'sube' : 'baja'} ${formatMoney(Math.abs(capacityChange))}.`}{' '}
        Esta cantidad es el límite menos el gasto variable registrado; no es el saldo de tus cuentas
        ni el estimado de cierre del mes.
      </p>
    </aside>
  );
}
